import { getSupabaseClient } from "./index";
import type { DeliveryFeeRule, Tenant } from "@veorest/types";

export type TenantUpdatePatch = Partial<
  Pick<
    Tenant,
    | "name"
    | "country_code"
    | "currency_code"
    | "currency"
    | "timezone"
    | "locale"
    | "tax_rate"
    | "logo_url"
    | "cover_url"
    | "address"
    | "phone"
    | "ai_name"
    | "escalate_phone"
    | "customer_ai_enabled"
    | "public_store_enabled"
    | "ai_voice_gender"
    | "ai_voice_speed"
    | "ai_custom_rules"
    | "business_hours"
    | "location_lat"
    | "location_lng"
  >
>;

export type DeliveryFeeRuleInput = {
  min_km: number;
  max_km: number;
  fee_amount: number;
  sort_order: number;
};

export type WhatsAppConfigRecord = {
  id: string;
  tenant_id: string;
  phone_number_id: string;
  waba_id: string;
  display_phone_number?: string | null;
  status?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
};

export type WhatsAppConfigInput = {
  phone_number_id: string;
  waba_id: string;
  display_phone?: string;
  is_active?: boolean;
};

function apiBaseUrl(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return (env?.VITE_API_BASE_URL || "").replace(/\/$/, "");
}

async function authHeaders(): Promise<Record<string, string>> {
  const supabase = getSupabaseClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

export async function getWhatsAppConfig(tenantId: string): Promise<WhatsAppConfigRecord | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("tenant_whatsapp_configs")
    .select(
      "id, tenant_id, phone_number_id, waba_id, display_phone_number, status, is_active, created_at, updated_at"
    )
    .eq("tenant_id", tenantId)
    .maybeSingle();

  if (error) throw error;
  return (data as WhatsAppConfigRecord | null) ?? null;
}

/**
 * Meta Embedded Signup — primary Connect path.
 * Exchanges FB.login code + postMessage IDs via OELOX backend.
 */
export async function embeddedSignupWhatsApp(
  tenantId: string,
  input: {
    code: string;
    wabaId?: string;
    phoneNumberId?: string;
    businessAccountId?: string;
    finishType?: string;
    registrationPin?: string;
  }
): Promise<{
  ok: boolean;
  connected: boolean;
  live: boolean;
  verifiedName?: string;
  config: {
    phoneNumberId: string;
    wabaId: string;
    displayPhoneNumber?: string | null;
    status?: string;
  };
  webhookUrl?: string;
  metaReminder?: string;
}> {
  const base = apiBaseUrl();
  if (!base) throw new Error("VITE_API_BASE_URL is not configured");
  const headers = await authHeaders();
  const res = await fetch(`${base}/v1/whatsapp/embedded-signup`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      tenantId,
      code: input.code,
      wabaId: input.wabaId,
      phoneNumberId: input.phoneNumberId,
      businessAccountId: input.businessAccountId,
      finishType: input.finishType,
      registrationPin: input.registrationPin,
    }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || "WhatsApp Embedded Signup failed");
  return json;
}

export async function getWhatsAppConnectionStatus(tenantId: string): Promise<{
  connected: boolean;
  live: boolean;
  config: {
    phoneNumberId?: string;
    wabaId?: string;
    displayPhoneNumber?: string | null;
    status?: string;
    isActive?: boolean;
  } | null;
  liveMeta?: { displayPhoneNumber?: string; verifiedName?: string; error?: string };
  webhookUrl?: string;
}> {
  const base = apiBaseUrl();
  if (!base) throw new Error("VITE_API_BASE_URL is not configured");
  const headers = await authHeaders();
  const res = await fetch(
    `${base}/v1/whatsapp/connection?tenantId=${encodeURIComponent(tenantId)}`,
    { headers }
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || "Failed to load WhatsApp status");
  return json;
}

export async function disconnectWhatsAppConfig(tenantId: string): Promise<void> {
  const base = apiBaseUrl();
  if (base) {
    const headers = await authHeaders();
    const res = await fetch(`${base}/v1/whatsapp/disconnect`, {
      method: "POST",
      headers,
      body: JSON.stringify({ tenantId }),
    });
    if (res.ok) return;
  }

  // Fallback: direct Supabase update
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("tenant_whatsapp_configs")
    .update({ is_active: false, status: "inactive" })
    .eq("tenant_id", tenantId);
  if (error) throw error;
}


export async function updateTenant(tenantId: string, patch: TenantUpdatePatch): Promise<Tenant> {
  const supabase = getSupabaseClient();

  const assertHoursPersisted = (row: Tenant) => {
    if (patch.business_hours == null) return;
    const expected = patch.business_hours;
    const got = row.business_hours;
    if (!got) throw new Error("business_hours_not_persisted");
    const norm = (v: string) => {
      const m = String(v).match(/^(\d{1,2}):(\d{2})/);
      if (!m) return v;
      return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
    };
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ] as const;
    for (const day of days) {
      const e = expected[day];
      const g = got[day];
      if (!e && !g) continue;
      if (!e || !g || norm(e.open) !== norm(g.open) || norm(e.close) !== norm(g.close)) {
        throw new Error("business_hours_not_persisted");
      }
    }
  };

  const assertRulesPersisted = (row: Tenant) => {
    if (patch.ai_custom_rules == null) return;
    const expected = patch.ai_custom_rules;
    const got = Array.isArray(row.ai_custom_rules) ? row.ai_custom_rules : [];
    if (got.length !== expected.length) {
      throw new Error("ai_custom_rules_not_persisted");
    }
    for (let i = 0; i < expected.length; i++) {
      const e = expected[i]!;
      const g = got[i]!;
      if (g.trigger !== e.trigger || g.response !== e.response || g.enabled !== e.enabled) {
        throw new Error("ai_custom_rules_not_persisted");
      }
    }
  };

  // Branding URLs: write tenants row directly (RLS tenant_update). Also covered by RPC after 036.
  const keys = Object.keys(patch);
  const brandingOnly =
    keys.length > 0 &&
    keys.every((k) => k === "logo_url" || k === "cover_url");
  if (brandingOnly) {
    const { data, error } = await supabase
      .from("tenants")
      .update(patch)
      .eq("id", tenantId)
      .select("*")
      .single();
    if (error) throw error;
    return data as Tenant;
  }

  // Prefer SECURITY DEFINER RPC so JSONB business_hours always persists.
  const { data: rpcData, error: rpcError } = await supabase.rpc("update_tenant_settings", {
    p_tenant_id: tenantId,
    p_patch: patch,
  });

  if (!rpcError && rpcData) {
    const row = (Array.isArray(rpcData) ? rpcData[0] : rpcData) as Tenant;
    try {
      assertHoursPersisted(row);
      assertRulesPersisted(row);
      return row;
    } catch {
      /* stale RPC may ignore new JSONB keys — fall through to table update */
    }
  }

  const { data, error } = await supabase
    .from("tenants")
    .update(patch)
    .eq("id", tenantId)
    .select("*")
    .single();

  if (error) throw rpcError ?? error;
  assertHoursPersisted(data as Tenant);
  assertRulesPersisted(data as Tenant);
  return data as Tenant;
}

const BRANDING_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Upload restaurant cover or logo to public menu-images bucket; returns public URL. */
export async function uploadTenantBrandingImage(
  tenantId: string,
  file: File,
  kind: "cover" | "logo" = "cover"
): Promise<string> {
  if (file.size > BRANDING_IMAGE_MAX_BYTES) {
    throw new Error("image_too_large");
  }
  const supabase = getSupabaseClient();
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const path = `${tenantId}/branding/${kind}-${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("menu-images").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("menu-images").getPublicUrl(path);
  return data.publicUrl;
}

export async function listDeliveryFeeRules(tenantId: string): Promise<DeliveryFeeRule[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("delivery_fee_rules")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("min_km", { ascending: true });

  if (error) throw error;
  return (data ?? []) as DeliveryFeeRule[];
}

// ── Effective delivery bands (partner read-only vs client editable) ──────────
export type EffectiveDeliveryFeeBand = {
  min_km: number;
  max_km: number;
  fee_amount: number;
  sort_order?: number;
};

export type EffectiveDeliveryFeeBands = {
  source: "partner" | "client";
  read_only: boolean;
  partner_id: string | null;
  partner_name: string | null;
  bands: EffectiveDeliveryFeeBand[];
};

/**
 * The rates that actually price this business: partner bands (read-only) when
 * a partner manages dispatch, else the business's own editable rules.
 */
export async function fetchEffectiveDeliveryFeeBands(
  tenantId: string
): Promise<EffectiveDeliveryFeeBands> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("tenant_effective_delivery_fee_bands", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const raw = (data ?? {}) as Record<string, unknown>;
  const bandsRaw = Array.isArray(raw.bands) ? raw.bands : [];
  return {
    source: raw.source === "partner" ? "partner" : "client",
    read_only: raw.read_only === true,
    partner_id: (raw.partner_id as string | null) ?? null,
    partner_name: (raw.partner_name as string | null) ?? null,
    bands: bandsRaw.map((b) => {
      const row = (b ?? {}) as Record<string, unknown>;
      return {
        min_km: Number(row.min_km ?? 0),
        max_km: Number(row.max_km ?? 0),
        fee_amount: Number(row.fee_amount ?? 0),
        sort_order: Number(row.sort_order ?? 0),
      };
    }),
  };
}

// ── Dispatch authority (who dispatches for this business) ─────────────────────
export type TenantDispatchAuthority = {
  managed_by_partner: boolean;
  partner_id: string | null;
  partner_name: string | null;
};

/**
 * Whether a partner manages delivery for this business. Partner-managed
 * clients cannot add drivers — dispatch is on the partner side.
 */
export async function fetchTenantDispatchAuthority(
  tenantId: string
): Promise<TenantDispatchAuthority> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("tenant_get_dispatch_authority", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const raw = (data ?? {}) as Record<string, unknown>;
  return {
    managed_by_partner: raw.managed_by_partner === true,
    partner_id: (raw.partner_id as string | null) ?? null,
    partner_name: (raw.partner_name as string | null) ?? null,
  };
}

export async function createDeliveryFeeRule(
  tenantId: string,
  input: DeliveryFeeRuleInput
): Promise<DeliveryFeeRule> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("delivery_fee_rules")
    .insert([{ tenant_id: tenantId, ...input }])
    .select("*")
    .single();

  if (error) throw error;
  return data as DeliveryFeeRule;
}

export async function updateDeliveryFeeRule(
  ruleId: string,
  input: DeliveryFeeRuleInput
): Promise<DeliveryFeeRule> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("delivery_fee_rules")
    .update(input)
    .eq("id", ruleId)
    .select("*")
    .single();

  if (error) throw error;
  return data as DeliveryFeeRule;
}

export async function deleteDeliveryFeeRule(ruleId: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("delivery_fee_rules").delete().eq("id", ruleId);
  if (error) throw error;
}
